import axios from "axios";
import { BASE_URLS } from "../core/data/redux/apis/urls";
import { tokenService } from "../core/utils/tokenService";

const profileImageCache = new Map<string, string>();
const inFlightRequests = new Map<string, Promise<string | null>>();

/**
 * Returns cached image URL if already fetched and available.
 */
export const getCachedProfileImage = (empIdOrPath?: string): string | null => {
  if (!empIdOrPath) return null;
  const key = empIdOrPath.trim();
  return profileImageCache.get(key) || null;
};

/**
 * Fetches authenticated profile image for an employee ID or relative path (e.g. /noc/chat/profile-image/:empId).
 * Converts binary JPEG blob into an Object URL and caches it in memory.
 */
export const fetchProfileImage = async (empIdOrPath?: string): Promise<string | null> => {
  if (!empIdOrPath) return null;
  const input = empIdOrPath.trim();

  // If already a complete direct URL, object URL, or data URI
  if (
    input.startsWith("data:") ||
    input.startsWith("http://") ||
    input.startsWith("https://") ||
    input.startsWith("blob:")
  ) {
    return input;
  }

  // Check if raw base64 string without data prefix
  if (input.length > 200 && !input.includes("/") && !input.includes(" ")) {
    const dataUrl = `data:image/jpeg;base64,${input}`;
    profileImageCache.set(input, dataUrl);
    return dataUrl;
  }

  // Check memory cache
  if (profileImageCache.has(input)) {
    return profileImageCache.get(input)!;
  }

  // Check in-flight promise to avoid duplicate concurrent requests
  if (inFlightRequests.has(input)) {
    return inFlightRequests.get(input)!;
  }

  const promise = (async () => {
    try {
      let relativePath = input;
      if (!relativePath.includes("/")) {
        relativePath = `noc/chat/profile-image/${relativePath}`;
      } else if (relativePath.startsWith("/")) {
        relativePath = relativePath.substring(1);
      }

      const baseUrl = BASE_URLS.BASE_URL.endsWith("/")
        ? BASE_URLS.BASE_URL
        : `${BASE_URLS.BASE_URL}/`;
      const fullUrl = `${baseUrl}${relativePath}`;

      const token = tokenService.getToken();
      const headers: Record<string, string> = {};
      if (token && token !== "null" && token !== "undefined") {
        headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      }

      const response = await axios.get(fullUrl, {
        headers,
        responseType: "blob",
      });

      if (response.data && response.data.size > 0) {
        const objectUrl = URL.createObjectURL(response.data);
        profileImageCache.set(input, objectUrl);
        // Also cache against the empId if path contained empId
        const parts = input.split("/");
        const empId = parts[parts.length - 1];
        if (empId && empId !== input) {
          profileImageCache.set(empId, objectUrl);
        }
        return objectUrl;
      }
      return null;
    } catch (error) {
      // In case of 404 or missing profile photo
      return null;
    } finally {
      inFlightRequests.delete(input);
    }
  })();

  inFlightRequests.set(input, promise);
  return promise;
};
